import React, { useEffect, useMemo, useState } from "react";
import { LuArrowLeft, LuCheck, LuEye, LuEyeOff, LuLoader, LuX } from "react-icons/lu";
import toast from "react-hot-toast";
import ConnectorLogo from "../connectors/ConnectorLogo";
import { FREQUENCIES } from "../connectors/catalog";
import { getApiErrorMessage, runConnectorTest, saveConnector } from "../connectors/integrationsService";

const TEAL = "#37B5A0";
const STEPS = [
  { id: 1, label: "Info" },
  { id: 2, label: "Connection" },
  { id: 3, label: "Test" },
  { id: 4, label: "Review" },
];

const STEP_SUBTITLES = {
  1: "Step 1 of 4 — Connector Info",
  2: "Step 2 of 4 — Connector Info",
  3: "Step 3 of 4 — Connector Info",
  4: "Step 4 of 4 — Connector Info",
};

function hostFromUrl(value) {
  if (!value) return "—";
  return String(value).replace(/^https?:\/\//i, "").replace(/\/$/, "") || value;
}

function maskSecret(value) {
  if (!value) return "••••••••••••";
  return "•".repeat(Math.max(12, Math.min(value.length, 16)));
}

function isFieldVisible(field, values) {
  if (!field.visibleWhen) return true;
  return Object.entries(field.visibleWhen).every(([key, expected]) => values[key] === expected);
}

function testFailureTitle(testResult) {
  const code = String(testResult?.errorCode || "").toUpperCase();
  if (code === "PROVIDER_UNAVAILABLE") return "Connection failed — Provider unavailable";
  if (code === "TIMEOUT" || code === "ETIMEDOUT") return "Connection failed — Timed out";
  return "Connection failed — Invalid credentials";
}

function fieldsFromValues(values) {
  return Object.keys(values || {})
    .filter((key) => !["has_client_secret", "has_password"].includes(key) && !String(key).endsWith("_masked"))
    .map((key) => ({
      key,
      label: String(key)
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .replace(/^\s/, "")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      secret: /secret|password|token/i.test(key),
    }));
}

function displayType(connector) {
  return connector.type || connector.name || "this connector";
}

function fieldDefaults(connector) {
  const defaults = {};
  (connector.fields || []).forEach((field) => {
    if (field.defaultValue != null) defaults[field.key] = field.defaultValue;
  });
  return defaults;
}

function Stepper({ step, testPassed }) {
  return (
    <div className="px-6 pt-1 pb-5">
      <div className="flex items-center">
        {STEPS.map((item, index) => {
          const complete =
            item.id === 3 ? testPassed && step > 3 : item.id < 3 ? step > item.id : step > item.id && testPassed;
          const active = step === item.id;
          const isLast = index === STEPS.length - 1;
          return (
            <React.Fragment key={item.id}>
              <div className="flex flex-col items-center w-12 flex-shrink-0">
                <div
                  className={`h-8 w-8 rounded-full flex items-center justify-center text-[12px] font-semibold ${
                    complete
                      ? "bg-[#528416] text-white"
                      : active
                      ? "bg-[#192536] text-white"
                      : "bg-white text-[#898F99] border border-[#E6E7EB]"
                  }`}
                >
                  {complete ? <LuCheck className="h-4 w-4" strokeWidth={3} /> : item.id}
                </div>
              </div>
              {!isLast && (
                <div className={`flex-1 h-[2px] mx-1 ${complete ? "bg-[#528416]" : "bg-[#E6E7EB]"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="flex items-start mt-1.5">
        {STEPS.map((item, index) => {
          const active = step === item.id;
          return (
            <React.Fragment key={item.id}>
              <div
                className={`w-12 flex-shrink-0 text-center text-[11px] leading-[14px] ${
                  active ? "font-semibold text-[#031F36]" : "text-[#898F99]"
                }`}
              >
                {item.label}
              </div>
              {index < STEPS.length - 1 && <div className="flex-1" />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[14px] leading-4 font-medium text-[#031F36] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full h-[42px] rounded-lg border border-[#E6E7EB] bg-white px-3 text-[14px] leading-4 text-[#031F36] placeholder:text-[#898F99] focus:outline-none focus:border-[#898F99]";

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <span className="text-[12px] leading-[14px] text-[#898F99]">{label}</span>
      <span className="text-[12px] leading-[14px] font-semibold text-[#031F36] text-right break-all">{value}</span>
    </div>
  );
}

function ReviewCard({ title, onEdit, children }) {
  return (
    <div className="rounded-[10px] border border-[#E6E7EB] overflow-hidden bg-white">
      <div className="flex items-center justify-between px-4 py-2.5">
        <span className="text-[14px] leading-4 font-semibold text-[#031F36]">{title}</span>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="text-[12px] leading-[14px] font-medium text-[#2563EB] hover:underline"
          >
            Edit
          </button>
        ) : null}
      </div>
      <div className="divide-y divide-[#F3F4F6]">{children}</div>
    </div>
  );
}

function chipClass(selected) {
  return `h-9 rounded-lg text-[13px] font-medium border ${
    selected
      ? "bg-[#D8F9F0] text-[#37B5A0] border-[#37B5A0]"
      : "bg-white border-[#E6E7EB] text-[#031F36] hover:bg-[#F6F7FA]"
  }`;
}

export default function ConnectConnectorDrawer({ connector, existing, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [showSecret, setShowSecret] = useState(false);
  const [testStatus, setTestStatus] = useState("idle");
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(existing?.name || connector.name);
  const [description, setDescription] = useState(existing?.description || "");
  const [frequency, setFrequency] = useState(existing?.frequency || "Hourly");
  const [values, setValues] = useState(() => ({
    ...fieldDefaults(connector),
    ...(existing?.config || {}),
  }));

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const fields =
    (connector.fields || []).length > 0 ? connector.fields : fieldsFromValues(values);

  const instruction =
    connector.instruction ||
    `Fields below are specific to ${displayType(connector)}. Enter the credentials for the instance you want to connect.`;

  const setField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setTestStatus("idle");
    setTestResult(null);
  };

  const summary = useMemo(() => {
    const keys = connector.testSummaryKeys || [];
    if (keys.length) {
      return keys.map((item) => {
        if (item.static) return { label: item.label, value: item.static };
        const raw = values[item.key];
        const value = item.display === "host" ? hostFromUrl(raw) : raw || "—";
        return { label: item.label, value };
      });
    }
    const rows = [];
    const urlField = fields.find((f) => /url|host/i.test(String(f.key)));
    if (urlField) {
      rows.push({
        label: urlField.label || "URL",
        value: hostFromUrl(values[urlField.key]),
      });
    } else {
      const first = fields.find((f) => !f.secret && f.type !== "segment");
      if (first) {
        rows.push({ label: first.label || first.key, value: values[first.key] || "—" });
      }
    }
    rows.push({
      label: "Auth Method",
      value: values.authType || connector.authMethod || connector.authLabel || displayType(connector) || "—",
    });
    return rows;
  }, [connector.testSummaryKeys, connector.authMethod, connector.authLabel, connector.type, connector.name, fields, values]);

  const goNext = () => {
    if (step === 1 && !name.trim()) {
      toast.error("Connector name is required");
      return;
    }
    if (step === 3 && testStatus !== "success") return;
    setStep((s) => Math.min(4, s + 1));
  };

  const handleTest = async () => {
    setTestStatus("loading");
    setTestResult(null);
    try {
      const result = await runConnectorTest({
        connectorId: existing?.id,
        type: connector.type,
        config: values,
      });
      setTestResult(result);
      setTestStatus(result.ok ? "success" : "error");
    } catch (err) {
      setTestResult({
        ok: false,
        result: "Failed",
        authStatus: "Rejected",
        latencyMs: 0,
        message: getApiErrorMessage(err, "Connection failed — Invalid credentials"),
      });
      setTestStatus("error");
    }
  };

  const handleSave = async () => {
    const savedName = name.trim() || connector.name;
    setSaving(true);
    try {
      await saveConnector({
        existingId: existing?.id,
        name: savedName,
        type: connector.type,
        description: description.trim(),
        frequency,
        config: values,
        enabled: existing?.enabled !== false,
      });
      if (existing?.id) {
        toast.success(`${savedName} updated`);
      } else {
        toast.custom((t) => (
          <div
            className={`${t.visible ? "opacity-100" : "opacity-0"} w-[347px] bg-white shadow-md border border-[#E6E7EB] rounded-lg p-4 flex items-start gap-3`}
          >
            <span className="h-8 w-8 rounded-full bg-[#528416] text-white flex items-center justify-center flex-shrink-0">
              <LuCheck className="h-4 w-4" strokeWidth={3} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] leading-4 font-semibold text-[#031F36]">Connector Added</p>
              <p className="text-[12px] leading-[14px] text-[#898F99] mt-1">
                {savedName} was connected successfully. First sync will run according to its schedule, or start it manually now.
              </p>
            </div>
            <button
              type="button"
              onClick={() => toast.dismiss(t.id)}
              className="text-[#898F99] hover:text-[#031F36] flex-shrink-0"
              aria-label="Dismiss"
            >
              <LuX className="h-4 w-4" />
            </button>
          </div>
        ));
      }
      onSaved?.();
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not save ${savedName}`));
    } finally {
      setSaving(false);
    }
  };

  const primaryDisabled = saving || (step === 3 && testStatus !== "success");

  const footerPrimaryLabel =
    step === 1
      ? "Next: Connection Config"
      : step === 2
      ? "Next: Test Connection"
      : step === 3
      ? "Next: Review & Save"
      : "Save Connector";

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close drawer"
        onClick={onClose}
      />
      <aside className="relative h-full w-full max-w-[480px] bg-white shadow-2xl flex flex-col animate-drawerIn">
        <div className="px-6 pt-5 pb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <ConnectorLogo type={connector.type} initials={connector.initials} />
            <div className="min-w-0">
              <h2 className="text-[16px] leading-5 font-semibold text-[#031F36]">
                {existing ? `Edit ${existing.name || connector.name}` : `Connect ${displayType(connector)}`}
              </h2>
              <p className="text-[12px] leading-[14px] text-[#898F99] mt-0.5">{STEP_SUBTITLES[step]}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#898F99] hover:text-[#031F36] p-1 -mr-1 flex-shrink-0"
            aria-label="Close"
          >
            <LuX className="h-5 w-5" />
          </button>
        </div>

        <Stepper step={step} testPassed={testStatus === "success"} />

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {step === 1 && (
            <div className="space-y-5">
              <Field label="Connector Name">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Description (optional)">
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this connector used for?"
                  className={inputClass}
                />
              </Field>
              <div>
                <p className="text-[14px] leading-4 font-medium text-[#031F36] mb-1.5">Sync Frequency</p>
                <div className="grid grid-cols-4 gap-2">
                  {FREQUENCIES.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setFrequency(opt)}
                      className={chipClass(frequency === opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                <p className="text-[12px] leading-[14px] text-[#8A8F98] mt-2">
                  You can change this later from the connector's detail page.
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-[14px] leading-4 text-[#8A8F98]">{instruction}</p>
              {fields.filter((field) => isFieldVisible(field, values)).map((field) => (
                field.type === "segment" ? (
                  <div key={field.key}>
                    <p className="text-[14px] leading-4 font-medium text-[#031F36] mb-1.5">{field.label}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {field.options.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setField(field.key, opt)}
                          className={`min-h-9 h-auto py-2 px-2 ${chipClass(
                            (values[field.key] || field.defaultValue) === opt
                          )}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                <Field key={field.key} label={field.label}>
                  {field.secret ? (
                    <div className="relative">
                      <input
                        type={showSecret ? "text" : "password"}
                        value={values[field.key] || ""}
                        onChange={(e) => setField(field.key, e.target.value)}
                        placeholder={field.placeholder || "••••••••••••"}
                        className={`${inputClass} pr-10`}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#898F99] hover:text-[#031F36]"
                        aria-label={showSecret ? "Hide secret" : "Show secret"}
                      >
                        {showSecret ? <LuEyeOff className="h-4 w-4" /> : <LuEye className="h-4 w-4" />}
                      </button>
                    </div>
                  ) : (
                    <input
                      value={values[field.key] || ""}
                      onChange={(e) => setField(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className={inputClass}
                    />
                  )}
                </Field>
                )
              ))}
              <p className="text-[12px] leading-[14px] text-[#8A8F98]">
                Stored encrypted. Masked everywhere in the UI, including on this screen after saving.
              </p>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="text-[16px] leading-5 font-semibold text-[#031F36]">Test Connection</h3>
              <p className="text-[14px] leading-4 text-[#8A8F98] mt-1">
                We'll verify these credentials against {displayType(connector)} before you can save.
              </p>
              <div className="mt-4 rounded-lg border border-[#E6E7EB] bg-white p-4 space-y-2.5">
                {summary.map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-3">
                    <span className="text-[14px] leading-4 text-[#8A8F98]">{row.label}</span>
                    <span className="text-[14px] leading-4 font-semibold text-[#031F36] text-right">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              {testStatus === "error" && (
                <div className="mt-4 rounded-lg border border-[#CD0F0F] bg-[#FCEBEB] p-3 flex items-start gap-3">
                  <LuX className="h-5 w-5 text-[#CD0F0F] flex-shrink-0" strokeWidth={2.5} />
                  <div className="min-w-0">
                    <p className="text-[14px] leading-4 font-semibold text-[#CD0F0F]">
                      {testFailureTitle(testResult)}
                    </p>
                    <p className="text-[12px] leading-[14px] text-[#CD0F0F] mt-1">
                      {testResult?.message ||
                        `${connector.name} rejected the provided credentials. Double-check the values and try again.`}
                    </p>
                  </div>
                </div>
              )}

              {testStatus === "success" && (
                <div className="mt-4 rounded-lg border border-[#528416] bg-[#EAF3DE] p-3 flex items-start gap-3">
                  <LuCheck className="h-5 w-5 text-[#528416] flex-shrink-0" strokeWidth={2.5} />
                  <div className="min-w-0">
                    <p className="text-[14px] leading-4 font-semibold text-[#528416]">
                      Connection successful
                    </p>
                    <p className="text-[12px] leading-[14px] text-[#528416] mt-1">
                      {testResult?.message ||
                        `Authenticated with ${connector.name} and verified read access to the target objects.`}
                    </p>
                  </div>
                </div>
              )}

              {(testStatus === "error" || testStatus === "success") && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div className="rounded-lg border border-[#E2E8F0] bg-[#F6F7FA] p-4">
                    <p className="text-[12px] leading-[14px] text-[#8A8F98]">Auth Status</p>
                    <p
                      className={`text-[14px] leading-4 font-semibold mt-1 ${
                        testStatus === "success" ? "text-[#528416]" : "text-[#CD0F0F]"
                      }`}
                    >
                      {testStatus === "success" ? "Valid" : "Rejected"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-[#E2E8F0] bg-[#F6F7FA] p-4">
                    <p className="text-[12px] leading-[14px] text-[#8A8F98]">Latency</p>
                    <p className="text-[14px] leading-4 font-semibold text-[#031F36] mt-1">
                      {testResult?.latencyMs != null ? `${testResult.latencyMs}ms` : "—"}
                    </p>
                  </div>
                  <div className="rounded-lg border border-[#E2E8F0] bg-[#F6F7FA] p-4">
                    <p className="text-[12px] leading-[14px] text-[#8A8F98]">Result</p>
                    <p
                      className={`text-[14px] leading-4 font-semibold mt-1 ${
                        testStatus === "success" ? "text-[#528416]" : "text-[#CD0F0F]"
                      }`}
                    >
                      {testStatus === "success" ? "Success" : "Failed"}
                    </p>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleTest}
                disabled={testStatus === "loading"}
                className="mt-4 w-full h-10 rounded-lg text-white text-[14px] leading-4 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: TEAL }}
              >
                {testStatus === "loading" && <LuLoader className="h-4 w-4 animate-spin" />}
                {testStatus === "idle" || testStatus === "loading"
                  ? "Test Connection"
                  : testStatus === "error"
                  ? "Retry Test Connection"
                  : "Test Connection Again"}
              </button>
              {testStatus === "idle" && (
                <p className="text-[12px] leading-[14px] text-[#8A8F98] mt-2">
                  No test run yet. Click 'Test Connection' to verify these credentials.
                </p>
              )}
              {testStatus === "loading" && (
                <p className="text-[12px] leading-[14px] text-[#8A8F98] mt-2">
                  Contacting {displayType(connector)} and verifying credentials — this usually takes a few
                  seconds...
                </p>
              )}
            </div>
          )}

          {step === 4 && (
            <div>
              <h3 className="text-[16px] leading-5 font-semibold text-[#031F36]">Review & Save</h3>
              <p className="text-[14px] leading-4 text-[#8A8F98] mt-1">
                Confirm the details below before saving this connector.
              </p>
              <div className="mt-4 space-y-3">
                <ReviewCard title="Connector Info" onEdit={() => setStep(1)}>
                  <ReviewRow label="Name" value={name || connector.name} />
                  <ReviewRow label="Description" value={description.trim() || "—"} />
                  <ReviewRow label="Sync Frequency" value={frequency} />
                </ReviewCard>
                <ReviewCard title="Connection Config" onEdit={() => setStep(2)}>
                  {summary.map((row) => (
                    <ReviewRow key={row.label} label={row.label} value={row.value} />
                  ))}
                  <ReviewRow
                    label={values.password ? "Password" : "Client Secret"}
                    value={maskSecret(values.clientSecret || values.password)}
                  />
                </ReviewCard>
                <ReviewCard title="Test Result">
                  <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-[12px] leading-[14px] text-[#898F99]">Status</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EAF3DE] text-[#528416] px-2.5 py-0.5 text-[12px] leading-[14px] font-medium">
                      <LuCheck className="h-3.5 w-3.5" />
                      Verified
                    </span>
                  </div>
                  <ReviewRow
                    label="Latency"
                    value={testResult?.latencyMs ? `${testResult.latencyMs}ms` : "—"}
                  />
                </ReviewCard>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E6E7EB] flex items-center justify-between gap-3">
          {step === 1 ? (
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-lg border border-[#031F36] bg-white text-[14px] leading-4 font-medium text-[#031F36] hover:bg-[#F6F7FA]"
            >
              Cancel
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="h-10 px-4 rounded-lg border border-[#031F36] bg-white text-[14px] leading-4 font-medium text-[#031F36] hover:bg-[#F6F7FA] inline-flex items-center gap-2"
            >
              <LuArrowLeft className="h-4 w-4" />
              Back
            </button>
          )}
          <button
            type="button"
            disabled={primaryDisabled}
            onClick={step === 4 ? handleSave : goNext}
            className="h-10 px-4 rounded-lg text-[14px] leading-4 font-medium text-white disabled:bg-[#E5E7EB] disabled:text-[#898F99] disabled:cursor-not-allowed"
            style={!primaryDisabled ? { backgroundColor: TEAL } : undefined}
          >
            {saving ? "Saving…" : footerPrimaryLabel}
          </button>
        </div>
      </aside>
    </div>
  );
}
